import {PlusIcon} from '@primer/octicons-react'
import {Button, Label, VisuallyHidden} from '@primer/react'
import {useCallback, useRef} from 'react'

import {normalizeConnectionStatus, pluginDisplayName, type Plugin} from '../types/connectors'
import {ConnectionStatusIcon, connectionStatusLabel} from './ConnectionStatusIcon'
import {ConnectorLogo} from './ConnectorLogo'
import styles from './ConnectorRow.module.css'

export interface ConnectorRowProps {
  plugin: Plugin
  /** Opens the detail dialog. The row body is the trigger. */
  onSelect: (plugin: Plugin) => void
  /** Starts authorization straight from the row, without opening the dialog. */
  onConnect: (plugin: Plugin) => void
  /** Shows a spinner on the connect button while authorization is in flight. */
  busy?: boolean
}

/**
 * One connector in a catalog list. The row body is a button whose overlay covers
 * the row, so the connect button beside it stays a sibling rather than a nested
 * control. Connect starts authorization directly; disconnect lives in the dialog.
 */
export function ConnectorRow({plugin, onSelect, onConnect, busy = false}: ConnectorRowProps) {
  const name = pluginDisplayName(plugin)
  const status = plugin.connection?.status
  const notConnected = normalizeConnectionStatus(status) === 'not_connected'
  const detailsButtonRef = useRef<HTMLButtonElement>(null)
  const connectButtonRef = useCallback((button: HTMLButtonElement | null) => {
    if (!button) return

    return () => {
      // Keep the user's place only when the removed Connect button has focus.
      if (document.activeElement === button) detailsButtonRef.current?.focus()
    }
  }, [])

  return (
    <div className={styles.row}>
      <button
        ref={detailsButtonRef}
        type="button"
        className={styles.trigger}
        onClick={() => onSelect(plugin)}
        aria-label={`${name}, ${connectionStatusLabel(status)}. View details`}
      >
        <ConnectorLogo src={plugin.logo} name={name} />
        <span className={styles.body}>
          <span className={styles.heading}>
            <span className={styles.name}>{name}</span>
            <Label variant="secondary">Connector</Label>
          </span>
          {plugin.description ? <span className={styles.description}>{plugin.description}</span> : null}
        </span>
      </button>
      <span className={styles.trailing}>
        {notConnected ? (
          <Button
            ref={connectButtonRef}
            size="small"
            variant="invisible"
            className={styles.connect}
            leadingVisual={PlusIcon}
            loading={busy}
            loadingAnnouncement={`Connecting ${name}`}
            onClick={() => onConnect(plugin)}
          >
            {/* Primer points `aria-labelledby` at the children while loading, which
                would otherwise drop the connector name from the accessible name. */}
            Connect<VisuallyHidden>{` ${name}`}</VisuallyHidden>
          </Button>
        ) : (
          <ConnectionStatusIcon status={status} />
        )}
      </span>
    </div>
  )
}
